/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform - Admin
 📄 File: pages/SetupPage.tsx
 🧩 Description:
    Initial setup page for creating the first admin password.
    Only shown when admin user has no password set.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Shield, CheckCircle2 } from 'lucide-react';
import { useBranding } from '../hooks/useBranding';

export default function SetupPage() {
    const navigate = useNavigate();
    const { brandingData } = useBranding();
    const branding = brandingData?.brandingConfig;

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);

    // Helper function to convert hex (including 8-digit with alpha) to rgba
    const hexToRgba = (hex: string): string => {
        if (!hex) return hex;
        // If already rgba, return as-is
        if (hex.startsWith('rgba') || hex.startsWith('rgb')) return hex;

        hex = hex.replace(/^#/, '');

        let r = 0, g = 0, b = 0, a = 1;

        if (hex.length === 6) {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        } else if (hex.length === 8) {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
            a = parseInt(hex.substring(6, 8), 16) / 255;
        } else {
            return `#${hex}`; // Return as-is if not 6 or 8 digits
        }

        return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
    };

    // Get branding values with fallbacks
    const appName = branding?.appName || 'Ring2All Admin';
    const primaryColor = branding?.primaryColor || '#3B82F6';
    const loginBackgroundColor = branding?.loginBackgroundColor || '#1a1a2e';
    const loginFontColor = branding?.loginFontColor || '#ffffff';

    // Convert colors with transparency to rgba for better browser support
    const loginFormBackground = hexToRgba(branding?.loginFormBackground || '#FFFFFF1A');
    const loginSubmitButtonColor = hexToRgba(branding?.loginSubmitButtonColor || '#3B82F6');

    const loginBackgroundImage = branding?.loginBackgroundImage;
    const disableLoginBackgroundImage = branding?.disableLoginBackgroundImage || false;
    const footerText = branding?.footerText || `Ring2All © ${new Date().getFullYear()}`;

    // Check if setup is needed
    useEffect(() => {
        const checkSetupStatus = async () => {
            try {
                const response = await fetch('/api/auth/setup-status');
                const data = await response.json();

                if (!data.needsSetup) {
                    // Password already set, redirect to login
                    navigate('/login');
                }
            } catch (err) {
                console.error('Failed to check setup status:', err);
            } finally {
                setCheckingStatus(false);
            }
        };

        checkSetupStatus();
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!password || password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch('/api/auth/setup/admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to set admin password');
            }

            setSuccess(true);

            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setError(err.message || 'Failed to set admin password');
        } finally {
            setIsLoading(false);
        }
    };

    // Build background style
    const backgroundStyle: React.CSSProperties = {
        backgroundColor: loginBackgroundColor,
    };

    if (loginBackgroundImage && !disableLoginBackgroundImage) {
        backgroundStyle.backgroundImage = `url(${loginBackgroundImage})`;
        backgroundStyle.backgroundSize = 'cover';
        backgroundStyle.backgroundPosition = 'center';
        backgroundStyle.backgroundRepeat = 'no-repeat';
    }

    if (checkingStatus) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: loginBackgroundColor }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
            </div>
        );
    }

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
            style={backgroundStyle}
        >
            {/* Background overlay for readability */}
            <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)' }}
            />

            {/* Animated background elements (only if no background image) */}
            {!loginBackgroundImage && (
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
                </div>
            )}

            <div className="w-full max-w-md relative z-10">
                <div
                    className="relative backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl"
                    style={{
                        backgroundColor: loginFormBackground,
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)'
                    }}
                >
                    {/* Logo Section - Priority: loginLogo > logo > Icon */}
                    <div className="text-center mb-8">
                        {branding?.loginLogo ? (
                            <div className="w-full mb-6 flex items-center justify-center">
                                <img
                                    src={branding.loginLogo}
                                    alt={appName}
                                    className="w-full h-24 object-contain drop-shadow-lg"
                                />
                            </div>
                        ) : branding?.logo ? (
                            <div className="w-full mb-6 flex items-center justify-center">
                                <img
                                    src={branding.logo}
                                    alt={appName}
                                    className="w-full h-24 object-contain drop-shadow-lg"
                                />
                            </div>
                        ) : (
                            <div
                                className="w-full h-24 rounded-2xl flex items-center justify-center mb-6 shadow-lg"
                                style={{
                                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
                                    boxShadow: `0 0 30px ${primaryColor}40`
                                }}
                            >
                                <Shield className="h-10 w-10 text-white mr-3" />
                                <span className="text-2xl font-bold text-white">
                                    {appName} Setup
                                </span>
                            </div>
                        )}
                        <h1 className="text-2xl font-bold" style={{ color: loginFontColor }}>
                            Initial Setup
                        </h1>
                        <p className="text-lg" style={{ color: `${loginFontColor}aa` }}>
                            Set the administrator password to get started
                        </p>
                    </div>

                    {success ? (
                        <div className="text-center">
                            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-white mb-2">Setup Complete!</h3>
                            <p className="text-white/70">Redirecting to login...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Password Field */}
                            <div className="space-y-2">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5" style={{ color: `${loginFontColor}b3` }} />
                                    </div>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-12 py-4 border border-white/20 rounded-xl placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300"
                                        placeholder="Password"
                                        autoComplete="new-password"
                                        style={{
                                            background: 'transparent',
                                            backdropFilter: 'blur(8px)',
                                            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                            color: loginFontColor,
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors"
                                        style={{ color: `${loginFontColor}99` }}
                                    >
                                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Confirm Password Field */}
                            <div className="space-y-2">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5" style={{ color: `${loginFontColor}b3` }} />
                                    </div>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-4 border border-white/20 rounded-xl placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300"
                                        placeholder="Confirm Password"
                                        autoComplete="new-password"
                                        style={{
                                            background: 'transparent',
                                            backdropFilter: 'blur(8px)',
                                            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                            color: loginFontColor,
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-xl backdrop-blur-sm">
                                    <p className="text-red-200 text-sm">{error}</p>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-4 px-6 rounded-xl font-bold text-xl focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 hover:shadow-2xl active:scale-95 group relative overflow-hidden"
                                style={{
                                    background: `linear-gradient(135deg, ${loginSubmitButtonColor} 0%, ${loginSubmitButtonColor}dd 100%)`,
                                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    color: loginFontColor,
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform -skew-x-12 -translate-x-full group-hover:translate-x-full"></div>

                                {isLoading ? (
                                    <div className="flex items-center justify-center relative z-10">
                                        <Loader2 className="h-6 w-6 animate-spin mr-3" />
                                        <span className="text-xl font-bold">Setting Password...</span>
                                    </div>
                                ) : (
                                    <span className="relative z-10 text-xl font-bold">Set Admin Password</span>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="absolute bottom-4 left-0 right-0 text-center z-10">
                <p className="text-sm" style={{ color: `${loginFontColor}66` }}>
                    {footerText}
                </p>
            </div>
        </div>
    );
}
